/**
 * The realtime client — Step 60.
 *
 * ONE socket for the whole tab, shared by every component that wants a topic.
 * A socket per component would mean N handshakes, N presence entries for one
 * person, and N reconnect storms when the server restarts.
 *
 * ── Signals, not entities ──────────────────────────────────────────────
 *
 * The server sends `{type:'ticket.updated', ticketId}` and nothing else about
 * the ticket. The client re-asks through the ordinary authorised route, which
 * is what decides whether the row still belongs in the view — the plan's
 * reasoning, and the reason this is an order of magnitude less code than a
 * sync engine and cannot drift.
 *
 * ── Coalesced ──────────────────────────────────────────────────────────
 *
 * Signals are batched ~250ms before they reach a subscriber. A bulk update
 * touching forty tickets fires forty times; without this the queue would
 * refetch forty times, which is worse than the polling it replaced.
 *
 * ── It is allowed to be down ───────────────────────────────────────────
 *
 * `isLive()` drives a `refetchInterval` fallback at the call site. The socket
 * is an optimisation over polling; a page that breaks when it drops has made
 * it a dependency, which is the failure this design exists to avoid.
 */
export interface Signal {
  type: 'ticket.updated' | 'ticket.created';
  ticketId: string;
  actorId: string | null;
  actorName: string | null;
}

type Handler = (signal: Signal) => void;
type PresenceHandler = (people: { userId: string; name: string }[]) => void;

const COALESCE_MS = 250;

let ws: WebSocket | null = null;
let live = false;
/** Backoff, capped: a server that is down must not be hammered by every open
 *  tab, and a fixed short retry from fifty tabs is a denial of service we
 *  wrote ourselves. */
let retryMs = 1_000;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

const changeHandlers = new Map<string, Set<Handler>>();
const presenceHandlers = new Map<string, Set<PresenceHandler>>();
/**
 * topic → the LAST signal seen for it in this window.
 *
 * Keyed by topic so forty updates to one ticket wake a subscriber once, and
 * the last one wins because the attribution people care about is "who touched
 * it most recently" — an older name would be stale the moment it was shown.
 */
const pending = new Map<string, [string, Signal]>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const liveListeners = new Set<(live: boolean) => void>();

function setLive(next: boolean) {
  if (live === next) return;
  live = next;
  for (const l of liveListeners) l(next);
}

function flush() {
  flushTimer = null;
  const batch = [...pending.values()];
  pending.clear();
  for (const [topic, signal] of batch) {
    for (const h of changeHandlers.get(topic) ?? []) h(signal);
  }
}

function send(payload: unknown) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
}

function connect() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  // Same origin, same cookies — the server authorises the upgrade with the
  // session, so there is no token to mint, leak or forget to rotate.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  ws = new WebSocket(`${proto}//${window.location.host}/ws`);

  ws.onopen = () => {
    retryMs = 1_000;
    setLive(true);
    // Re-subscribe everything. A reconnect that silently forgets its topics
    // is worse than staying down: the page LOOKS live and is not.
    for (const topic of changeHandlers.keys()) send({ type: 'subscribe', topic });
  };

  ws.onmessage = (ev) => {
    let msg: { type?: string; ticketId?: string; channel?: string; people?: unknown };
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if ((msg.type === 'ticket.updated' || msg.type === 'ticket.created') && msg.ticketId) {
      const signal: Signal = {
        type: msg.type,
        ticketId: msg.ticketId,
        actorId: (msg as { actorId?: string | null }).actorId ?? null,
        actorName: (msg as { actorName?: string | null }).actorName ?? null,
      };
      // Delivered to the ticket's own subscribers AND to anyone watching the
      // org firehose, which is how a list learns about a ticket it is not
      // individually subscribed to.
      for (const topic of [`ticket:${msg.ticketId}`, 'org']) {
        if (changeHandlers.has(topic)) pending.set(topic, [topic, signal]);
      }
      if (!flushTimer) flushTimer = setTimeout(flush, COALESCE_MS);
      return;
    }
    if (msg.type === 'presence' && typeof msg.channel === 'string') {
      // The channel arrives org-prefixed; the client only ever knows topics.
      const topic = msg.channel.slice(msg.channel.indexOf('|') + 1);
      const people = Array.isArray(msg.people) ? (msg.people as { userId: string; name: string }[]) : [];
      for (const h of presenceHandlers.get(topic) ?? []) h(people);
    }
  };

  const down = () => {
    setLive(false);
    ws = null;
    if (retryTimer) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      retryMs = Math.min(retryMs * 2, 30_000);
      if (changeHandlers.size || presenceHandlers.size) connect();
    }, retryMs);
  };
  ws.onclose = down;
  ws.onerror = down;
}

/**
 * Watch a topic. Returns the unsubscribe.
 *
 * Both handlers are optional so a component can take presence without
 * invalidation or the other way round.
 */
export function subscribe(
  topic: string,
  handlers: { onChange?: Handler; onPresence?: PresenceHandler },
): () => void {
  const first = !changeHandlers.has(topic) && !presenceHandlers.has(topic);
  if (handlers.onChange) {
    if (!changeHandlers.has(topic)) changeHandlers.set(topic, new Set());
    changeHandlers.get(topic)!.add(handlers.onChange);
  }
  if (handlers.onPresence) {
    if (!presenceHandlers.has(topic)) presenceHandlers.set(topic, new Set());
    presenceHandlers.get(topic)!.add(handlers.onPresence);
  }
  connect();
  if (first) send({ type: 'subscribe', topic });

  return () => {
    if (handlers.onChange) {
      const set = changeHandlers.get(topic);
      set?.delete(handlers.onChange);
      if (set && set.size === 0) changeHandlers.delete(topic);
    }
    if (handlers.onPresence) {
      const set = presenceHandlers.get(topic);
      set?.delete(handlers.onPresence);
      if (set && set.size === 0) presenceHandlers.delete(topic);
    }
    // Only when nothing in this tab still wants it — two components on one
    // ticket must not unsubscribe each other.
    if (!changeHandlers.has(topic) && !presenceHandlers.has(topic)) {
      send({ type: 'unsubscribe', topic });
    }
  };
}

export function isLive(): boolean {
  return live;
}

export function onLiveChange(fn: (live: boolean) => void): () => void {
  liveListeners.add(fn);
  return () => liveListeners.delete(fn);
}
